import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const planKeys = {
  all: ['plans'],
  lists: () => [...planKeys.all, 'list'],
  details: () => [...planKeys.all, 'detail'],
  detail: (id) => [...planKeys.details(), id],
};

export const usePlans = () => {
  return useQuery({
    queryKey: planKeys.lists(),
    queryFn: async () => {
      const response = await apiClient.get('plans');
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

export const usePlan = (id, options = {}) => {
  return useQuery({
    queryKey: planKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`plans/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options
  });
};

export const useCreatePlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (planData) => {
      const response = await apiClient.post('plans', planData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: planKeys.lists() });
    },
    onError: (error) => {
      console.error('Error creating plan:', error);
    }
  });
};

export const useUpdatePlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await apiClient.put(`plans/${id}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(planKeys.detail(variables.id), data);
      queryClient.invalidateQueries({ queryKey: planKeys.lists() });
    },
    onError: (error) => {
      console.error('Error updating plan:', error);
    }
  });
};

export const useDeletePlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`plans/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: planKeys.lists() });
      queryClient.removeQueries({ queryKey: planKeys.detail(deletedId) });
    },
    onError: (error) => {
      console.error('Error deleting plan:', error);
    }
  });
};